// Custom error types for LogManagement operations
// These provide better error categorization and handling for log management failures
// @ts-nocheck

/**
 * Base error class for all LogManagement-related errors
 */
export class LogManagementError extends Error {
  public readonly isLogManagementError = true;
  public readonly timestamp: string;
  public readonly context: string | undefined;
  public readonly retryable?: boolean;

  constructor(message: string, public originalError?: any, context?: string) {
    super(message);
    this.name = 'LogManagementError';
    this.timestamp = new Date().toISOString();
    this.context = context || undefined;
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    return this.message;
  }

  /**
   * Get technical details for debugging
   */
  getTechnicalDetails(): Record<string, any> {
    return {
      name: this.name,
      message: this.message,
      timestamp: this.timestamp,
      context: this.context,
      originalError: this.originalError
    };
  }

  /**
   * Get recovery action for this error
   */
  getRecoveryAction(): string {
    return 'retry';
  }

  /**
   * Get validation errors (override in validation error classes)
   */
  getValidationErrors(): Record<string, string[]> {
    return {};
  }
}

/**
 * Authentication error for LogManagement operations
 * Thrown when admin authentication is required or has failed
 */
export class LogManagementAuthError extends LogManagementError {
  public readonly requiresLogin = true;
  public readonly errorType = 'authentication';

  constructor(message: string = 'Authentication required for log management', originalError?: any) {
    super(message, originalError, 'authentication');
    this.name = 'LogManagementAuthError';
  }

  override getUserMessage(): string {
    return 'Please log in again to access log management features.';
  }

  override getRecoveryAction(): string {
    return 'redirect_to_login';
  }
}

/**
 * Permission error for LogManagement operations
 * Thrown when user lacks sufficient permissions
 */
export class LogManagementPermissionError extends LogManagementError {
  public readonly errorType = 'permission';

  constructor(message: string = 'Insufficient permissions for log management', originalError?: any) {
    super(message, originalError, 'permission');
    this.name = 'LogManagementPermissionError';
  }

  override getUserMessage(): string {
    return 'You do not have permission to access log management features.';
  }

  override getRecoveryAction(): string {
    return 'contact_admin';
  }
}

/**
 * Service unavailable error for LogManagement operations
 * Thrown when the LogManagement service is down or unavailable
 */
export class LogManagementServiceError extends LogManagementError {
  public readonly retryable = true;
  public readonly errorType = 'service';

  constructor(message: string = 'Log management service unavailable', originalError?: any) {
    super(message, originalError, 'service');
    this.name = 'LogManagementServiceError';
  }

  override getUserMessage(): string {
    return 'Log management service is currently unavailable. Please try again later.';
  }

  override getRecoveryAction(): string {
    return 'retry_later';
  }
}

/**
 * Network error for LogManagement operations
 * Thrown when network connectivity issues prevent API calls
 */
export class LogManagementNetworkError extends LogManagementError {
  public readonly retryable = true;
  public readonly errorType = 'network';

  constructor(message: string = 'Network error accessing log management', originalError?: any) {
    super(message, originalError, 'network');
    this.name = 'LogManagementNetworkError';
  }

  override getUserMessage(): string {
    return 'Please check your internet connection and try again.';
  }

  override getRecoveryAction(): string {
    return 'check_connection';
  }
}

/**
 * Endpoint not found error for LogManagement operations
 * Thrown when a LogManagement endpoint is not found (404)
 */
export class LogManagementEndpointError extends LogManagementError {
  public readonly errorType = 'endpoint';

  constructor(message: string = 'Log management endpoint not found', originalError?: any) {
    super(message, originalError, 'endpoint');
    this.name = 'LogManagementEndpointError';
  }

  override getUserMessage(): string {
    return 'The requested log management feature is not available.';
  }

  override getRecoveryAction(): string {
    return 'contact_support';
  }
}

/**
 * Rate limit error for LogManagement operations
 * Thrown when too many requests are made to LogManagement APIs
 */
export class LogManagementRateLimitError extends LogManagementError {
  public readonly retryable = true;
  public readonly errorType = 'rate_limit';
  public readonly retryAfter: number | undefined;

  constructor(message: string = 'Too many log management requests', originalError?: any, retryAfter?: number) {
    super(message, originalError, 'rate_limit');
    this.name = 'LogManagementRateLimitError';
    this.retryAfter = retryAfter || undefined;
  }

  override getUserMessage(): string {
    const waitTime = this.retryAfter ? ` Please wait ${this.retryAfter} seconds before trying again.` : '';
    return `Too many requests to log management service.${waitTime}`;
  }

  override getRecoveryAction(): string {
    return 'wait_and_retry';
  }
}

/**
 * Server error for LogManagement operations
 * Thrown when LogManagement APIs return 5xx errors
 */
export class LogManagementServerError extends LogManagementError {
  public readonly retryable = true;
  public readonly errorType = 'server';

  constructor(message: string = 'Log management server error', originalError?: any) {
    super(message, originalError, 'server');
    this.name = 'LogManagementServerError';
  }

  override getUserMessage(): string {
    return 'Log management server is experiencing issues. Please try again later.';
  }

  override getRecoveryAction(): string {
    return 'retry_later';
  }
}

/**
 * Data validation error for LogManagement operations
 * Thrown when log data fails validation
 */
export class LogManagementValidationError extends LogManagementError {
  public readonly errorType = 'validation';
  public readonly validationErrors: Record<string, string[]>;

  constructor(message: string, validationErrors: Record<string, string[]> = {}, originalError?: any) {
    super(message, originalError, 'validation');
    this.name = 'LogManagementValidationError';
    this.validationErrors = validationErrors;
  }

  override getUserMessage(): string {
    return 'The log data provided is invalid. Please check your input and try again.';
  }

  override getRecoveryAction(): string {
    return 'fix_input';
  }

  override getValidationErrors(): Record<string, string[]> {
    return this.validationErrors;
  }
}

/**
 * Type guard to check if an error is a LogManagement error
 */
export function isLogManagementError(error: any): error is LogManagementError {
  return error && error.isLogManagementError === true;
}

/**
 * Factory function to create appropriate LogManagement error from API response
 */
export function createLogManagementError(
  statusCode: number,
  message?: string,
  originalError?: any,
  endpoint?: string
): LogManagementError {
  const context = endpoint ? `endpoint: ${endpoint}` : undefined;

  switch (statusCode) {
    case 401:
      return new LogManagementAuthError(message, originalError);
    case 403:
      return new LogManagementPermissionError(message, originalError);
    case 404:
      return new LogManagementEndpointError(message, originalError);
    case 429:
      return new LogManagementRateLimitError(message, originalError);
    case 503:
      return new LogManagementServiceError(message, originalError);
    default:
      if (statusCode >= 500) {
        return new LogManagementServerError(message, originalError);
      } else if (statusCode >= 400) {
        return new LogManagementValidationError(message || 'Invalid request', {}, originalError);
      } else {
        return new LogManagementError(message || 'Unknown log management error', originalError, context);
      }
  }
}

/**
 * Get user-friendly error message and recovery action for any LogManagement error
 */
export function getErrorInfo(error: LogManagementError): {
  title: string;
  message: string;
  action: string;
  actionType: string;
} {
  if (error instanceof LogManagementAuthError) {
    return {
      title: 'Authentication Required',
      message: error.getUserMessage(),
      action: 'Login',
      actionType: 'redirect_to_login'
    };
  } else if (error instanceof LogManagementPermissionError) {
    return {
      title: 'Access Denied',
      message: error.getUserMessage(),
      action: 'Contact Admin',
      actionType: 'contact_admin'
    };
  } else if (error instanceof LogManagementServiceError) {
    return {
      title: 'Service Unavailable',
      message: error.getUserMessage(),
      action: 'Retry',
      actionType: 'retry_later'
    };
  } else if (error instanceof LogManagementNetworkError) {
    return {
      title: 'Network Error',
      message: error.getUserMessage(),
      action: 'Retry',
      actionType: 'check_connection'
    };
  } else if (error instanceof LogManagementRateLimitError) {
    return {
      title: 'Too Many Requests',
      message: error.getUserMessage(),
      action: 'Wait and Retry',
      actionType: 'wait_and_retry'
    };
  } else if (error instanceof LogManagementValidationError) {
    return {
      title: 'Invalid Data',
      message: error.getUserMessage(),
      action: 'Fix Input',
      actionType: 'fix_input'
    };
  } else {
    return {
      title: 'Error',
      message: error.getUserMessage(),
      action: 'Retry',
      actionType: 'retry'
    };
  }
}